import { NgClass } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { PanelModule } from 'primeng/panel';
import { ConfigurableButtonComponent } from '../components/button.component';
import { StepperComponent } from '../components/stepper.component';
import { TableComponent } from '../components/table.component';
import { CustomCodeEventDetail } from '../interfaces/reassignment.interface';
import { PUITranslatePipe } from '../pipes/p-ui-translate.pipe';
import { BulkOperationService } from '../services/bulk-operation.service';
import { NavigationService } from '../services/navigation.service';
import { ReassignmentService } from '../services/reassignment.service';
import { TableConfigBuilder } from '../services/table-config-builder.service';
declare var ap: any;
@Component({
    selector: 'app-before-reassignment-summary',
    standalone: true,
    styles: [
        `
            .p-20 {
                padding: 1.25rem;
            }
            .px-20 {
                padding-left: 1.25rem;
                padding-right: 1.25rem;
            }
        `,
    ],
    template: `
        @if (ready) {
            <div class="w-full h-full flex-column shadow-md surface-ground flex-1 flex overflow-auto">
                <div class="flex flex-column">
                    <div class="border-bottom-1 surface-border p-20 font-semibold">
                        {{ 'default-reassign-title' | pTranslate }}
                    </div>
                    <div class="w-full px-20 border-bottom-1 surface-border">
                        <platform-stepper [steps]="steps" [currentStepName]="currentStepName"></platform-stepper>
                    </div>
                </div>
                <!-- Table Section -->
                <div class="flex flex-column flex-1 border-bottom-1 surface-border">
                    <div class="flex flex-column gap-3 reassignment-cases p-3">
                        <p-panel
                            [toggleable]="true"
                            styleClass="shadow-none surface-border border-1 surface-ground overflow-hidden"
                        >
                            <ng-template pTemplate="header">
                                <div class="flex align-items-center gap-2">
                                    <span>
                                        {{
                                            'default-reassign-beforeReassignmentTitle'
                                                | pTranslate
                                                    : 'Updated Fields: The table summarizes changes to the ' +
                                                          reassignmentService.casesToReassign.length +
                                                          ' Tasks. Are you ready to continue?'
                                                    : { totalCases: reassignmentService.casesToReassign.length }
                                        }}
                                    </span>
                                </div>
                            </ng-template>
                            <ng-template let-isCollapsed pTemplate="headericons">
                                <i
                                    class="pi"
                                    [ngClass]="{ 'pi-chevron-up': !isCollapsed, 'pi-chevron-down': isCollapsed }"
                                ></i>
                            </ng-template>
                            <div class="m-0 surface-border border-top-1 surface-ground">
                                <div class="overflow-auto max-h-50rem">
                                    <platform-table
                                        [tableConfiguration]="tableData.tableConfiguration"
                                        [data]="tableData.data"
                                        [selectable]="tableConfig.selectable"
                                        (afterViewInitEvent)="handleAfterViewInit($event)"
                                        [customClass]="
                                            'reassignment-summary-table ' + reassignmentService.currentTableClass
                                        "
                                    ></platform-table>
                                </div>
                            </div>
                        </p-panel>
                    </div>
                </div>
                <!-- Comment Section -->
                <div class="flex flex-column gap-2 p-3">
                    <label for="comment">{{ 'default-reassign-comment' | pTranslate: 'Comment' }}</label>
                    <textarea
                        id="comment"
                        rows="3"
                        class="w-full p-2 border-1 border-round surface-border"
                        [(ngModel)]="comment"
                        placeholder="{{ 'default-reassign-commentPlaceholder' | pTranslate: 'Enter Comment...' }}"
                    ></textarea>
                </div>
                <div class="flex flex-column sticky bottom-0">
                    <!-- Button Section -->
                    <div class="w-full flex justify-content-between surface-ground border-top-1 surface-border p-3">
                        <div>
                            <platform-button
                                [buttonType]="'button'"
                                [label]="'default-reassign-buttonPrevious' | pTranslate: 'Previous'"
                                [styleClass]="'reassign-action-btn'"
                                [outlined]="true"
                                [icon]="'pi pi-angle-left'"
                                [iconPosition]="'left'"
                                (buttonClick)="goToPrevious()"
                            ></platform-button>
                        </div>
                        <div class="flex justify-content-end gap-3">
                            <platform-button
                                [buttonType]="'button'"
                                [text]="true"
                                [label]="'default-reassign-buttonCancel' | pTranslate: 'Cancel'"
                                (buttonClick)="reassignmentCancelButtonClick()"
                                [styleClass]="'reassign-action-btn'"
                            ></platform-button>
                            <platform-button
                                [buttonType]="'button'"
                                [styleClass]="'w-8rem'"
                                [label]="'default-reassign-buttonConfirm' | pTranslate: 'Confirm'"
                                (buttonClick)="confirmReassignment()"
                                [styleClass]="'reassign-action-btn'"
                            ></platform-button>
                        </div>
                    </div>
                </div>
            </div>
        }
    `,
    imports: [
        ConfigurableButtonComponent,
        StepperComponent,
        TableComponent,
        PanelModule,
        NgClass,
        FormsModule,
        PUITranslatePipe,
    ],
})
export class BeforeReassignmentSummaryComponent implements OnInit {
    reassignmentService = inject(ReassignmentService);
    navigationService = inject(NavigationService);
    bulkOperationService = inject(BulkOperationService);
    private messageService = inject(MessageService);

    steps = [
        { order: 1, name: 'default-reassign-beforeReassignment' },
        { order: 2, name: 'default-reassign-beforeReassignmentSummary' },
    ];
    currentStepName = 'default-reassign-beforeReassignmentSummary';
    newAssignee = '';
    searchText = '';
    comment = '';
    currentPage = 1;
    tableConfig: any;
    tableInstance: TableConfigBuilder | null = null;
    tableData: any;
    ready = false;
    private confirmationService = inject(ConfirmationService);

    ngOnInit() {
        if (this.reassignmentService.casesToReassign.length === 0) {
            this.navigationService.navigateToInbox();
        }

        this.init();
    }

    async init() {
        this.tableInstance = this.reassignmentService.buildBeforeReassignmentSummaryTable();
        this.tableData = await this.tableInstance.view();
        this.tableConfig = this.tableInstance.build();
        this.ready = true;
    }
    goToPrevious() {
        this.navigationService.navigateToReassignment();
    }

    confirmReassignment() {
        const bulkReassignmentRequest = this.reassignmentService.formatReassignmentDataForBulkOperation(
            false,
            this.comment,
        );
        this.bulkOperationService.performBulkReassignment(bulkReassignmentRequest).subscribe({
            next: (data) => {
                this.reassignmentService.operationId = data.id;
                this.navigationService.navigateToReassignmentTracker();
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    detail: error?.error?.message,
                    summary: 'Error',
                });
            },
        });
    }

    reassignmentCancelButtonClick() {
        this.confirmationService.confirm({
            message:
                ap.language.translateConfig('{{ lang.default-reassign-cancelConfirmationMessage }}') ||
                'Are you sure you want to cancel?',
            header: ap.language.translateConfig('{{ lang.default-reassign-confirm }}') || 'Confirm',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.navigationService.navigateToInbox();
            },
        });
    }

    async handleAfterViewInit(event: CustomCodeEventDetail) {
        await this.reassignmentService.executeAfterViewInitAction(event);
    }
}
